import { createElement, useEffect, useState, useCallback } from "react";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/dist/styles/ag-grid.css";
import "ag-grid-community/dist/styles/ag-theme-balham.css";
import { FiEdit, FiTrash2 } from "react-icons/fi";
import { IoIosAddCircleOutline } from "react-icons/io";

import { FacilityCreateEdit } from "@components/facility/FacilityCreateEdit";
import { IFacilityDTO } from "@interfaces/IFacilityDTO";
import { Box } from "@mui/material";
import FacilityService from "@service/FacilityService";
import { fetchPlants, PlantOption } from "@service/PlantService";
import { AgGridContextMenu } from "@components/ag_grid/AgGridContextMenu";

export function FacilityList(): JSX.Element {
  const [plants, setPlants] = useState<PlantOption[]>([]);
  const [selectedPlantId, setSelectedPlantId] = useState<number | null>(null);

  const [facilities, setFacilities] = useState<IFacilityDTO[]>([]);
  const [selectedFacility, setSelectedFacility] = useState<IFacilityDTO | null>(
    null
  );

  const [facility_id, setFacility_id] = useState<number>(-1);
  const [isLoading, setIsLoading] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);

  const breakpoint = 768; // Define the breakpoint for mobile view
  const [isMobile, setIsMobile] = useState(window.innerWidth < breakpoint);

  useEffect(() => {
    const onResize = () => {
      setIsMobile(window.innerWidth < breakpoint);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpoint, isMobile]);

  // Load the list of plants for the selector
  useEffect(() => {
    async function loadPlants() {
      try {
        const data = await fetchPlants();
        setPlants(data);
        // Auto-select the first plant and load its facilities
        if (data.length > 0) {
          setSelectedPlantId(data[0].id);
          setIsLoading(true);
        }
      } catch (error) {
        console.error("Error fetching plants:", error);
      }
    }
    loadPlants();
  }, []);

  // Load facilities whenever the selected plant changes (or a refresh is requested)
  const loadFacilities = useCallback(async (plantId: number) => {
    try {
      const data = await FacilityService.getFacilitiesByPlant(plantId);
      setFacilities(data);
    } catch (error) {
      console.error("Error fetching facilities:", error);
      setFacilities([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedPlantId !== null && isLoading) {
      loadFacilities(selectedPlantId);
    }
  }, [selectedPlantId, isLoading, loadFacilities]);

  const handlePlantChange = (plantId: number | null) => {
    setSelectedPlantId(plantId);
    if (plantId !== null) {
      setIsLoading(true);
    } else {
      setFacilities([]);
    }
  };

  const pageSize = 13;
  const defaultColDef = {
    sortable: true,
  };
  // Context menu hook
  const { handleCellContextMenu, ContextMenuComponent } = AgGridContextMenu({});

  // Prevent default browser context menu
  const handleContextMenu = useCallback((event: React.MouseEvent) => {
    event.preventDefault();
  }, []);

  const selectedPlantName =
    plants.find((p) => p.id === selectedPlantId)?.value ?? "";

  const plantColumnDefs = [
    // {
    //   headerName: "Id",
    //   field: "id",
    //   width: 60,
    //   filter: true,
    //   cellStyle: { textAlign: "center" },
    // },
    {
      headerName: "Plant",
      field: "value",
      flex: 1,
      minWidth: 150,
      filter: true,
    },
  ];

  const handlePlantRowClicked = (event: any): void => {
    handlePlantChange(event.data?.id ?? null);
  };

  const getPlantRowStyle = (params: any) => {
    if (params.data?.id === selectedPlantId) {
      return { background: "#b7d4ff" };
    }
    return undefined;
  };

  const handleEdit = (param: IFacilityDTO | null): void => {
    if (selectedPlantId === null) return;
    if (param !== null) {
      setFacility_id(param.id ?? 0);
      setSelectedFacility(param);
      setShowEditDialog(true);
    } else {
      setFacility_id(0);
      setSelectedFacility(null);
      setShowEditDialog(true);
    }
  };

  const handleDelete = async (facility: IFacilityDTO): Promise<void> => {
    if (!facility.id) return;
    if (!window.confirm(`Delete facility "${facility.type}"?`)) return;
    try {
      await FacilityService.deleteFacility(facility.id);
      setIsLoading(true); // Refresh the list for the selected plant
    } catch (error) {
      console.error("Error deleting facility:", error);
    }
  };

  const handleClose = () => {
    setShowEditDialog(false);
    setSelectedFacility(null);
    setFacility_id(-1);
    setIsLoading(true); // Refresh the list for the selected plant
  };

  const columnDefs = [
    {
      headerName: "Facility Id",
      field: "id",
      width: 120,
      filter: true,
      cellStyle: { textAlign: "center" },
    },
    {
      headerName: "Type",
      field: "type",
      width: 300,
      filter: true,
    },
    {
      headerName: "Invoice Code",
      field: "invoice_code",
      width: 180,
      filter: true,
      cellStyle: { textAlign: "center" },
    },
    {
      headerName: "Location Code",
      field: "location_code",
      width: 180,
      filter: true,
      cellStyle: { textAlign: "center" },
    },
    {
      headerName: "Edit",
      field: "action_edit",
      width: 65,
      cellRenderer: (params: any): JSX.Element => {
        return (
          <button
            className="tw-mt-2 tw-mb-2 tw-color-primary"
            onClick={() => handleEdit(params.data)}
          >
            {createElement(FiEdit as React.ComponentType<{ size?: number }>, {
              size: 16,
            })}
          </button>
        );
      },
      cellStyle: { textAlign: "center" },
    },
    {
      headerName: "Delete",
      field: "action_delete",
      width: 75,
      cellRenderer: (params: any): JSX.Element => {
        return (
          <button
            className="tw-mt-2 tw-mb-2 tw-text-red-600 hover:tw-opacity-70"
            onClick={() => handleDelete(params.data)}
          >
            {createElement(FiTrash2 as React.ComponentType<{ size?: number }>, {
              size: 16,
            })}
          </button>
        );
      },
      cellStyle: { textAlign: "center" },
    },
    {
      headerName: "Add",
      field: "action_add",
      width: 65,
      cellRenderer: (): JSX.Element => {
        return (
          <button
            className="tw-mt-2 tw-mb-2 tw-color-primary"
            onClick={() => handleEdit(null)}
          >
            {createElement(
              IoIosAddCircleOutline as React.ComponentType<{ size?: number }>,
              { size: 16 }
            )}
          </button>
        );
      },
      cellStyle: { textAlign: "center" },
    },
  ];

  return (
    <div className="tw-flex tw-flex-col tw-w-full tw-h-full">
      <Box
        sx={{
          padding: "1em",
        }}
      >
        <div className="tw-p-4 tw-bg-white tw-rounded-lg tw-shadow-md">
          <div className="tw-flex tw-justify-between tw-items-center tw-mb-4">
            <h1 className="tw-color-primary">Facilities</h1>
            <button
              className="tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2 tw-bg-blue-600 tw-text-white tw-rounded hover:tw-bg-blue-700 tw-transition-colors disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
              onClick={() => handleEdit(null)}
              disabled={selectedPlantId === null}
              title={
                selectedPlantId === null
                  ? "Select a plant first"
                  : "Add Facility"
              }
            >
              {createElement(
                IoIosAddCircleOutline as React.ComponentType<{ size?: number }>,
                { size: 20 }
              )}
              <span>Add Facility</span>
            </button>
          </div>

          {/* Plants and facilities side by side — facilities are scoped to the selected plant */}
          <div className="tw-flex tw-flex-col md:tw-flex-row tw-gap-4">
            {/* Plants grid — select a row to load its facilities */}
            <div className="tw-w-full md:tw-w-80 tw-flex-shrink-0">
              {/* <label className="tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1 tw-block">
                Plant
              </label> */}
              <div className="ag-theme-balham">
                <AgGridReact
                  className="ag-theme-balham"
                  domLayout="autoHeight"
                  columnDefs={plantColumnDefs}
                  rowHeight={40}
                  rowData={plants}
                  rowSelection="single"
                  defaultColDef={defaultColDef}
                  onRowClicked={handlePlantRowClicked}
                  getRowStyle={getPlantRowStyle}
                  animateRows
                />
              </div>
            </div>

            {/* Facilities for the selected plant */}
            <div className="tw-flex-1 tw-min-w-0">
              {selectedPlantId === null ? (
                <div className="tw-text-gray-500 tw-text-sm tw-py-8 tw-text-center tw-border tw-border-dashed tw-rounded">
                  Select a plant to view and manage its facilities.
                </div>
              ) : (
                <>
                  {!isLoading && (
                    <>
                      <div
                        className="tw-mb-4"
                        onContextMenu={handleContextMenu}
                      >
                        {!isMobile ? (
                          <>
                            <AgGridReact
                              className="ag-theme-balham"
                              domLayout="autoHeight"
                              columnDefs={columnDefs}
                              rowHeight={40}
                              rowData={facilities}
                              gridOptions={{}}
                              rowSelection="multiple"
                              defaultColDef={defaultColDef}
                              onCellContextMenu={handleCellContextMenu}
                              colResizeDefault="shift"
                              animateRows
                              pagination
                              pivotPanelShow="always"
                              paginationPageSize={pageSize}
                            />
                            {ContextMenuComponent}
                          </>
                        ) : (
                          <div className="tw-space-y-4">
                            {facilities.map((facility) => (
                              <div
                                key={facility.id}
                                className="tw-border tw-rounded-lg tw-p-4 tw-mb-4 tw-text-xs tw-shadow-md tw-leading-5"
                              >
                                <p>
                                  <strong>Type:</strong> {facility.type}
                                </p>
                                <p>
                                  <strong>Invoice Code:</strong>{" "}
                                  {facility.invoice_code}
                                </p>
                                <p>
                                  <strong>Location Code:</strong>{" "}
                                  {facility.location_code}
                                </p>
                                <div className="tw-flex tw-gap-4 tw-mt-2 tw-justify-start">
                                  <button
                                    className="tw-flex tw-color-primary tw-border tw-rounded-lg tw-p-2 tw-w-20 tw-text-xs tw-bg-gray-200"
                                    onClick={() => handleEdit(facility)}
                                  >
                                    {createElement(
                                      FiEdit as React.ComponentType<{
                                        size?: number;
                                      }>,
                                      { size: 16 }
                                    )}
                                    <span className="tw-ml-1">Edit</span>
                                  </button>
                                  <button
                                    className="tw-flex tw-text-red-600 tw-border tw-rounded-lg tw-p-2 tw-w-20 tw-text-xs tw-bg-gray-200"
                                    onClick={() => handleDelete(facility)}
                                  >
                                    {createElement(
                                      FiTrash2 as React.ComponentType<{
                                        size?: number;
                                      }>,
                                      { size: 16 }
                                    )}
                                    <span className="tw-ml-1">Delete</span>
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      {showEditDialog && (
                        <FacilityCreateEdit
                          id={facility_id}
                          selectedFacility={selectedFacility}
                          plantId={selectedPlantId}
                          plantName={selectedPlantName}
                          isOpen={showEditDialog}
                          onClose={handleClose}
                        />
                      )}
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </Box>
    </div>
  );
}
